import { Skeleton, Stack } from "@mui/material";
import FullScreenInit from "components/FullScreenInit";
import { usePtcldScriptContext } from "contexts/PtcldScriptContext";
import { useEffect, useRef, useState, useMemo } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import PointCloudView from "containers/PointCloudViewer/components/PointCloudView";
import { useGetOutputPointcloud } from "api/output/queries/useGetOutputPointcloud";
import PCLDViewerHeader from "./PointCloudViewerHeader";
import { useGetOutputQuery } from "api/output/queries/useGetOutputQuery";
import { useGetProjectQuery } from "api/project/queries/useGetProjectQuery";
import { getHTTPFileUrlFromS3Path, getHTTPUrlFromS3Path } from "utils/outputs";
import { useGetScanTransformationQuery } from "api/scan/queries/useGetScanTransformationQuery";
import { useLoading } from "contexts/LoadingContext";
import { Bounce, toast } from "react-toastify";
import BlurryOverlay from "components/BlurryOverlay";
import { useGetVirtualTourReferenceMeasurements } from "api/virtual-tour/queries/useGetVirtualTourReferenceMeasurements";
import { getMiniMapSize, STATE_SELECTION } from "utils/potree";
import { getPotreeCameraAtom } from "store/atoms/potree/getPotreeCamera";
import { useAtom } from "jotai";
import { Vector3 } from "three";
import { M_TO_FT_CONVERSION_FACTOR } from "utils/constants";

function PointCloudViewerContent() {
  const {
    scriptsLoaded,
    scriptError,
    setIfcLoading,
    error,
    setError,
    setIsSplit,
    isSplit,
    width,
    setIsIfcAvailable,
    setIsObjAvailable,
  } = usePtcldScriptContext();
  const {
    setLoading: setPointcloudLoading,
    setProgress: setLoadingProgression,
    blurryEffect,
    ptcldOutputId1,
    vtourOutputId1,
    scan1,
    scan1Data,
  } = useLoading();

  const [queryParams] = useSearchParams();

  const scan1FromQP = queryParams.get("scan1") || "";
  const scanFromQP = queryParams.get("scan") || "";

  const { scanId: scanIdFromP = "" } = useParams();

  const scanId = scanIdFromP || scan1FromQP || scanFromQP;

  const potreeContainerDiv = useRef<HTMLDivElement>(null);
  const viewerRef = useRef<Potree.Viewer | null>(null);
  const [searchParams, setSearchParams] = useSearchParams();
  const selectionQuery = searchParams.get("selection");
  const segmentationEnabled = searchParams.get("segmentation");

  const [potreeCamera, setPotreeCamera] = useAtom(getPotreeCameraAtom);
  

  const {
    data: pointCloudData,
    isLoading: dataLoading,
    error: dataError,
  } = useGetOutputPointcloud(ptcldOutputId1);
  const { data: output } = useGetOutputQuery(ptcldOutputId1);
  const { data: project } = useGetProjectQuery(output?.scan?.projectId || "");
  const { data: scanTransformation } = useGetScanTransformationQuery(scanId);
  const {
    data: vtouroutput,
    isLoading: outputLoading,
    error: outputError,
  } = useGetOutputQuery(vtourOutputId1);
  const { data: referenceMeasurements } =
    useGetVirtualTourReferenceMeasurements(vtouroutput?.virtualTour?.id || "");
  const scaleFactor = useMemo(() => {
    if (!referenceMeasurements || referenceMeasurements.length === 0) 
        return project?.metadata?.unitSystem === "imperial" ? M_TO_FT_CONVERSION_FACTOR : 1;

    const cumulativeTotalScaleFactor = referenceMeasurements.reduce(
      (prev, current) => prev + (current.metadata?.scaleFactor ?? 0),
      0
    );

    const finalScaleFactorWithoutUnitConverted = cumulativeTotalScaleFactor / referenceMeasurements.length;
    const finalScaleFactor = finalScaleFactorWithoutUnitConverted * (project?.metadata?.unitSystem === "imperial" ? M_TO_FT_CONVERSION_FACTOR : 1);
    
    return finalScaleFactor;
  }, [referenceMeasurements, project?.metadata?.unitSystem]);

  console.log("calculated scale factor:", scaleFactor, "project unit system:", project?.metadata?.unitSystem);

  const outputUrl = output?.s3Path
    ? getHTTPUrlFromS3Path(output.s3Path).replace(
        "pointcloud/",
        "virtualtour/"
      ) + "vtour/"
    : "";
  const transformJsonUrl = outputUrl + "transforms_sparse_octree_textured.json";
  const getTransformJson = async () =>
    await (await fetch(transformJsonUrl)).json();
  const thumbnailUrl = outputUrl + "thumbnail.png";

  // No dispatch needed here
  const objUrl = useMemo(() => {
    if (scan1Data?.referenceFloorPlanAlignedObjURL) {
      setIsObjAvailable(true);
    }
    return scan1Data?.referenceFloorPlanAlignedObjURL;
  }, [scan1Data]);

  const floorImageUrl = useMemo(() => {
    return scan1Data?.referenceFloorPlanAlignedImageURL;
  }, [scan1Data]);

  // useEffect(() => {
  //   if (segmentationEnabled) {
  //     selectionQuery 
  //   }
  // }, [segmentationEnabled]);

  // const {
  //   data: projectScanTransformation,
  //   isLoading: projectScanTransformationLoading,
  //   error: projectScanTransformationError,
  // } = useGetScanTransformationRelativeToProjectQuery(scanId);
  // console.log(projectScanTransformation);
  let matrix: number[][] = [];
  useEffect(() => {

    if (!project) return;

    const viewerElem = potreeContainerDiv.current;
    setLoadingProgression(0);
    if (!blurryEffect) {
      setPointcloudLoading(true);
    }
    let pointcloud: Potree.PointCloud | null = null;
    if (scriptsLoaded && pointCloudData) {
      // setPointcloudLoading(true); // eralier doing this but creating fluctation
      if (pointCloudData.ifc) setIsIfcAvailable(true);
      const Potree = window.Potree;
      const proj4 = window.proj4;
      if (!Potree) {
        setError("Potree library not found.");
        console.error("Potree library not found.");
        setPointcloudLoading(false);
        return;
      }
      if (viewerElem) {
        console.log("potree", Potree);
        viewerRef.current = new Potree.Viewer(viewerElem);
        if (scanTransformation) {
          matrix = scanTransformation.scanWrtWorld;
          // matrix = [[2, 0, 0, 0], [0, 2, 0, 0], [0, 0, 2, 0], [0, 0, 0, 1]];
        }

        const Image360 = async (coordinatesUrl: string, imageUrl: string) => {
          try {
            if (viewerRef.current) {
              proj4.defs(
                "WGS84",
                "+proj=longlat +ellps=WGS84 +datum=WGS84 +no_defs"
              );
              proj4.defs("pointcloud", viewerRef.current.getProjection());
              const transform = proj4("WGS84", "pointcloud");

              const params = {
                transform: transform,
              };

              // Load 360 images
              Potree.Images360Loader.load(
                coordinatesUrl,
                viewerRef.current,
                imageUrl,
                params,
                matrix
              ).then((images) => {
                console.log("Loaded 360 images", images);
                if (viewerRef.current) {
                  viewerRef.current.scene.add360Images(images);
                }
              });
            }
          } catch (err) {
            console.error("Error in run function:", err);
          }
        };

        const OrientedImage = async (
          coordinatesUrl: string,
          imageUrls: { [key: string]: string | null },
          xmlfile: string
        ) => {
          try {
            // Load oriented images
            if (viewerRef.current) {
              Potree.OrientedImageLoader.load(
                xmlfile,
                coordinatesUrl,
                viewerRef.current,
                imageUrls
              ).then((images) => {
                if (viewerRef.current) {
                  viewerRef.current.scene.addOrientedImages(images);
                }
              });
            }
          } catch (err) {
            console.error("Error in run function:", err);
          }
        };

        try {
          if (viewerRef.current) {
            viewerRef.current.setEDLEnabled(true);
            viewerRef.current.setFOV(60);
            viewerRef.current.setPointBudget(1 * 1000 * 1000);
            viewerRef.current.setClipTask(Potree.ClipTask.HIGHLIGHT);
            viewerRef.current.loadSettingsFromURL();
            viewerRef.current.setBackground("black");

            viewerRef.current.setControls(viewerRef.current.orbitControls);

            viewerRef.current.loadGUI(() => {
              if (viewerRef.current) {
                viewerRef.current.setLanguage("en");
                
                // Set measurement unit based on project settings
                const projectUnitSystem = project?.metadata?.unitSystem || "metric";
                console.log("Project unit system:", projectUnitSystem);
                const lengthUnit = projectUnitSystem === "metric" ? "m" : "ft";
                console.log("Setting length unit to:", lengthUnit);
                viewerRef.current.setLengthUnit(lengthUnit);
                
                const menuAppearance =
                  document.getElementById("menu_appearance");
                if (menuAppearance && menuAppearance.nextElementSibling) {
                  const sibling =
                    menuAppearance.nextElementSibling as HTMLElement;
                  sibling.style.display = "block";
                } else {
                  console.error("Next sibling of #menu_appearance not found.");
                }
              }
            });

            const url = getHTTPFileUrlFromS3Path(pointCloudData.metadata);
            const hierarchyUrl = getHTTPFileUrlFromS3Path(
              pointCloudData.hierarchy
            );
            const octreeUrl = getHTTPFileUrlFromS3Path(pointCloudData.octree);
            const otherPath = {
              octree: octreeUrl,
              hierarchy: hierarchyUrl,
            };

            Potree.loadPointCloud(url, otherPath, matrix).then(
              (e) => {
                if (viewerRef.current) {
                  if (
                    objUrl &&
                    floorImageUrl &&
                    scan1Data?.scanTransformation?.referenceFloor?.metadata
                  ) {
                    viewerRef.current.setFloorPlan(
                      floorImageUrl,
                      {
                        x: scan1Data.scanTransformation?.referenceFloor
                          ?.metadata?.obj?.centerX,
                        y: scan1Data.scanTransformation?.referenceFloor
                          ?.metadata?.obj?.centerY,
                      },
                      scan1Data.scanTransformation?.referenceFloor?.metadata
                        ?.obj?.lengthX,
                      scan1Data.scanTransformation?.referenceFloor?.metadata
                        ?.obj?.lengthY
                    );
                    viewerRef.current.miniMapPosition(
                      undefined,
                      10,
                      getMiniMapSize(),
                      floorImageUrl,
                      {
                        x: scan1Data.scanTransformation?.referenceFloor
                          ?.metadata?.obj?.centerX,
                        y: scan1Data.scanTransformation?.referenceFloor
                          ?.metadata?.obj?.centerY,
                      },
                      scan1Data.scanTransformation?.referenceFloor?.metadata
                        ?.obj?.lengthX,
                      scan1Data.scanTransformation?.referenceFloor?.metadata
                        ?.obj?.lengthY
                    );
                    viewerRef.current.floorPlanEnabled = true;
                    viewerRef.current.thumbnailEnabled = false;
                  }
                  getTransformJson()
                    .then((data) => {
                      if ("thumbnail" in data) {
                        console.log("Thumbnail data exists:", data.thumbnail);
                        viewerRef.current?.setThumbnail(
                          thumbnailUrl,
                          {
                            x: data.thumbnail.center[0],
                            y: data.thumbnail.center[1],
                          },
                          data.thumbnail.width,
                          data.thumbnail.height
                        );
                        if (viewerRef.current && !viewerRef.current?.floorPlanEnabled) {
                          try {
                            viewerRef.current?.miniMapPosition(
                              undefined,
                              10,
                              getMiniMapSize(),
                              thumbnailUrl,
                              {
                                x: data.thumbnail.center[0],
                                y: data.thumbnail.center[1],
                              },
                              data.thumbnail.width,
                              data.thumbnail.height
                            );
                            viewerRef.current.thumbnailEnabled = true;
                            viewerRef.current.floorPlanEnabled = false;
                          } catch (e) {
                            console.error(
                              "Error setting minimap image in Potree:",
                              e
                            );
                          }
                        }
                      } else {
                        console.log("No thumbnail data found in JSON");
                      }
                    })
                    .catch((error) =>
                      console.error("Error fetching transform JSON:", error)
                    );

                  pointcloud = e.pointcloud;
                  pointcloud.name = "ptcld";
                  const material = pointcloud.material;

                  material.activeAttributeName = segmentationEnabled ? "classification" : "rgba";
                  material.minSize = 2;
                  material.size = 0.5;
                  material.pointSizeType = Potree.PointSizeType.ADAPTIVE;
                  if (scaleFactor && scaleFactor !== 0) {
                    viewerRef.current.setExternalMeasureScale(scaleFactor);
                  }
                  viewerRef.current.scene.addPointCloud(pointcloud);
                  viewerRef.current.fitToScreen();
                  viewerRef.current.setPointBudget(5_000_000);
                  // viewerRef.current.useHQ = true;

                  // 360 images check
                  if (
                    pointCloudData.coordinates360 &&
                    pointCloudData.vtImage360Path
                  ) {
                    const coordinates360path = getHTTPFileUrlFromS3Path(
                      pointCloudData.coordinates360
                    );
                    const images360path = getHTTPUrlFromS3Path(
                      pointCloudData.vtImage360Path
                    );
                    Image360(coordinates360path, images360path);
                    viewerRef.current.setClassifications(
                      Potree.ClassificationScheme.PREIMAGE
                    );
                  }

                  // Oriented/simple images check
                  if (
                    pointCloudData.coordinatesOriented &&
                    pointCloudData.xmlfile &&
                    pointCloudData.imageOriented
                  ) {
                    OrientedImage(
                      pointCloudData.coordinatesOriented,
                      pointCloudData.imageOriented,
                      pointCloudData.xmlfile
                    );
                  }

                  // viewerRef.current.mapView.showSources(false);
                  setLoadingProgression(100);
                }
              },
              (e) => {
                console.error("ERROR loading point cloud:", e);
                setPointcloudLoading(false);
              }
            );

            // if segmentation is enabled, dont show the ifc
            if (pointCloudData.ifc) {
              setIfcLoading(true);


              // const ifcLoader = new IFCLoader();
              // ifcLoader.ifcManager.setWasmPath('../libs/three.js/extra/ifc/');

              const ifc = new Potree.IFC(viewerRef.current);
              const ifcUrl = getHTTPFileUrlFromS3Path(pointCloudData.ifc);
              console.log("ifcUrl", ifcUrl);
              ifc
                .load(ifcUrl, viewerRef.current.scene, "ifc", true, matrix)
                .then(async (model: any) => {

                  
                  console.log("model loaded", model)
                  if (segmentationEnabled) {
                    model.visible = false;
                  }
                  
                  //   const unit = await ifcLoader.ifcManager.getAllItemsOfType(
                  //     model.modelID,
                  //     IFCUNITASSIGNMENT,
                  //     false
                  //   )
                  //   console.log(unit)
                  //   const unitAssignment = await ifcLoader.ifcManager.getItemProperties(
                  //     model.modelID,
                  //     unit[0]
                  //   )
                  //   console.log(unitAssignment)
            
                  //   let lengthUnit = null;
                  //   let scaleFactor = 1.0;
            
                  //   // Check each unit in the Units array
                  //   for (let i = 0; i < unitAssignment.Units.length; i++) {
                  //     const unitRef = unitAssignment.Units[i];
                  //     const unitId = unitRef.value;
            
                  //     // Get the properties of each unit
                  //     const unit = await ifcLoader.ifcManager.getItemProperties(
                  //       model.modelID,
                  //       unitId
                  //     );
            
                  //     console.log(`Unit ${i} (ID: ${unitId}):`, unit);
            
                  //     // Check if this is a length unit
                  //     if (unit.UnitType && unit.UnitType.value === 'LENGTHUNIT') {
                  //       console.log("🎯 Found length unit!");
                  //       console.log("Unit Name:", unit.Name?.value);
                  //       console.log("Unit Prefix:", unit.Prefix?.value);
                  //       console.log("Full unit object:", unit);
            
                  //       lengthUnit = unit;
            
                  //       // Determine scale factor based on the unit
                  //       const unitName = unit.Name?.value;
                  //       const prefix = unit.Prefix?.value;
            
                  //       if (unitName === 'METRE') {
                  //         if (prefix === 'MILLI') {
                  //           scaleFactor = 0.001; // millimeters to meters
                  //         } else if (prefix === 'CENTI') {
                  //           scaleFactor = 0.01; // centimeters to meters
                  //         } else {
                  //           scaleFactor = 1.0; // meters
                  //         }
                  //       } else if (unitName === 'FOOT') {
                  //         scaleFactor = 0.3048; // feet to meters
                  //       } else if (unitName === 'INCH') {
                  //         scaleFactor = 0.0254; // inches to meters
                  //       } else {
                  //         console.warn(`Unknown unit: ${prefix || ''}${unitName}`);
                  //         scaleFactor = 1.0;
                  //       }
            
                  //       console.log(`Scale factor determined: ${scaleFactor}`);
                  //       break; // Found the length unit, no need to continue
                  //     }
                  //   }
            
                  //   if (lengthUnit) {
                  //     console.log(`Applying scale factor: ${scaleFactor} for unit: ${lengthUnit.Prefix?.value || ''}${lengthUnit.Name?.value}`);
                  //     model.mesh.scale.multiplyScalar(scaleFactor);
                  //   } else {
                  //     console.log("No length unit found, using default scaling");
                  //     model.mesh.scale.multiplyScalar(0.3048); // Default to feet-to-meters
                  //   }
                    setIfcLoading(false);
                  
                });
            }

            // Potree.loadPointCloud(url, otherPath).then(
            //   (e) => {
            //     if (viewerRef.current) {
            //       const pointcloud = e.pointcloud;
            //       pointcloud.name = "split-ptcld";
            //       pointcloud.visible = false;
            //       const material = pointcloud.material;

            //       material.activeAttributeName = "rgba";
            //       material.minSize = 2;
            //       material.pointSizeType = Potree.PointSizeType.FIXED;

            //       viewerRef.current.scene2.addPointCloud(pointcloud);
            //       viewerRef.current.fitToScreen();
            //       viewerRef.current.mapView.showSources(false);
            //       setPointcloudLoading(false);
            //     }
            //   },
            //   (e) => {
            //     console.error("ERROR loading point cloud:", e);
            //     setPointcloudLoading(false);
            //   }
            // );
          }
        } catch (err) {
          setError("Failed to initialize Potree viewer.");
          console.error("Error loading point cloud:", err);
          setPointcloudLoading(false);
        }
      }
    }
    return () => {
      document
        .querySelectorAll(".sp-container, .ui-slider, .i18n-container")
        .forEach((element) => {
          element.remove();
        });
      console.log("pointcloud", pointcloud);
      if (pointcloud) {
        console.log("Disposing pointcloud");
        Potree.cleanupPotree(pointcloud);
      }

      if (viewerRef.current) {
        console.log("Viewer ref exists, clearing it", viewerRef.current);
        viewerRef.current.renderer.clear();
        viewerRef.current = null;

        const profileWindow = document.getElementById("profile_window");
        if (profileWindow && profileWindow.parentNode) {
          profileWindow.parentNode.removeChild(profileWindow);
        }
      }
    };
  }, [pointCloudData, scriptsLoaded, project]);

  // unit change handling useEffect
  useEffect(() => {
    // set unit
    viewerRef.current?.setLengthUnit(project?.metadata?.unitSystem === 'imperial' ? 'ft' : 'm');
    // update the scale factor
    if (!scaleFactor || scaleFactor === 0) return;
    viewerRef.current?.setExternalMeasureScale(scaleFactor);
  }, [project?.metadata?.unitSystem, scaleFactor])

  useEffect(() => {
    if (selectionQuery === STATE_SELECTION.ifc) {
      if (
        viewerRef.current?.scene2?.ifc?.length &&
        viewerRef.current.scene2.ifc.length > 0
      )
        return;
      if (pointCloudData?.ifc && viewerRef.current) {
        const toastId = toast.loading("Loading IFC...", {
          position: "bottom-right",
          style: { width: "auto" },
          transition: Bounce,
        });
        const ifc = new Potree.IFC(viewerRef.current);
        const ifcUrl = getHTTPFileUrlFromS3Path(pointCloudData.ifc);
        // Load the IFC model with automatic unit detection
        // The IFC loader will try to detect units and apply the appropriate scale factor
        if (viewerRef.current) {
          console.log("Loading split IFC with automatic unit detection");
          
          // Load the split IFC model directly
          ifc
            .load(ifcUrl, viewerRef.current.scene2, "split-ifc", true, matrix)
            .then((splitMesh: unknown) => {
              toast.dismiss(toastId);
              console.log("Split IFC loaded with automatic scaling", splitMesh);
              
              // If the scale looks wrong, we could apply a correction here
              // But we'll let the IFC loader handle it first based on unit detection
            })
            .catch((error: any) => {
              toast.dismiss(toastId);
              toast.error("Error loading IFC model");
              console.error("Error loading IFC model:", error);
            });
        }
        
        console.log("Loading IFC model with optimized performance");
        
      }
    } else if (selectionQuery === STATE_SELECTION.obj) {
      if (
        viewerRef.current?.scene2?.obj?.length &&
        viewerRef.current.scene2.obj.length > 0
      )
        return;
      if (viewerRef.current && objUrl && floorImageUrl) {
        // pointCloudData?.ifc && viewerRef.current
        const toastId = toast.loading("Loading Floor Plan...", {
          position: "bottom-right",
          style: { width: "auto" },
          transition: Bounce,
        });
        const objLoader = new Potree.OBJModelLoader(viewerRef.current);
        // getHTTPFileUrlFromS3Path(pointCloudData.ifc)
        objLoader
          .load(
            objUrl,
            floorImageUrl,
            viewerRef.current?.scene,
            "floor",
            matrix
          )
          .then((object: Potree.Obj) => {
            console.log("floor plan loaded", object);
          })
          .catch((error: any) => {
            console.error("Error loading floor plan:", error);
          });

        objLoader
          .load(
            objUrl,
            floorImageUrl,
            viewerRef.current?.scene2,
            "split-floor",
            matrix
          )
          .then((object: Potree.Obj) => {
            toast.dismiss(toastId);
            console.log("floor plan loaded", object);
          })
          .catch((error: any) => {
            toast.dismiss(toastId);
            console.error("Error loading floor plan:", error);
          });
      }
    }
  }, [selectionQuery, viewerRef.current]);

  useEffect(() => {
    if (viewerRef.current) {
      // console.log()
      viewerRef.current.scene.view.setView(
        [potreeCamera.position.x, potreeCamera.position.y, potreeCamera.position.z],
        [potreeCamera.target.x, potreeCamera.target.y, potreeCamera.target.z],
      )

    }
  }, [potreeCamera])

  if (dataError || error || scriptError) {
    return toast.error(error || scriptError || dataError?.message, {
      position: "bottom-left",
      autoClose: 3000,
      hideProgressBar: false,
      closeOnClick: false,
      pauseOnHover: true,
      draggable: true,
      progress: undefined,
      theme: "dark",
      transition: Bounce,
    });
  }

  return (
    <Stack height="100%" direction="column" gap={{ xs: 0.5, sm: 1 }}>
      {/* {!fullScreen && (
        <PCLDViewerHeader />
      )} */}
      <PointCloudView
        potreeContainerDiv={potreeContainerDiv}
        viewerRef={viewerRef}
      />
    </Stack>
  );
}

export default PointCloudViewerContent;